import { useMemo } from 'react';
import { CrudPathParams, CrudResource } from '../models/crud.types';
import {
  useCrudCreateMutation,
  useCrudDeleteMutation,
  useCrudListQuery,
  useCrudUpdateMutation,
} from '../store/crud.api';

export const useCrud = <T extends object>(
  resource: CrudResource<T>,
  params?: CrudPathParams,
  query?: Record<string, string | number | boolean | null | undefined>,
  options?: {
    skipList?: boolean;
  }
) => {
  const apiResource = useMemo(
    () => ({
      endpoint: resource.endpoint,
      rowKey: resource.rowKey,
      transforms: resource.transforms,
    }),
    [resource.endpoint, resource.rowKey, resource.transforms]
  );

  const listArgs = useMemo(
    () => ({
      resource: apiResource,
      params,
      query,
    }),
    [apiResource, params, query]
  );

  const listQuery = useCrudListQuery(listArgs as any, {
    skip: !!options?.skipList,
  });
  const [createMutation, createState] = useCrudCreateMutation();
  const [updateMutation, updateState] = useCrudUpdateMutation();
  const [deleteMutation, deleteState] = useCrudDeleteMutation();

  const reload = () => listQuery.refetch();

  const create = (data: Partial<T>) => {
    return createMutation({
      resource: apiResource,
      params,
      data,
    }).unwrap() as Promise<T>;
  };

  const update = (id: string | number, data: Partial<T>) => {
    return updateMutation({
      resource: apiResource,
      params,
      id,
      data,
    }).unwrap() as Promise<T>;
  };

  const remove = (id: string | number) => {
    return deleteMutation({
      resource: apiResource,
      params,
      id,
    }).unwrap();
  };

  const loading =
    (!options?.skipList && (listQuery.isLoading || listQuery.isFetching)) ||
    createState.isLoading ||
    updateState.isLoading ||
    deleteState.isLoading;

  const error =
    (!options?.skipList ? (listQuery.error as any)?.message : null) ||
    (createState.error as any)?.message ||
    (updateState.error as any)?.message ||
    (deleteState.error as any)?.message ||
    null;

  return {
    data: (listQuery.data || []) as unknown as T[],
    loading,
    error,
    reload,
    create,
    update,
    remove,
  };
};
